import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ApiError, managementApi, type SectionKind } from '../lib/api'
import { fromLocalInput, toLocalInput, toCurrentOrigin } from '../lib/format'
import { Alert, CopyField, PageShell, Spinner } from '../components/ui'
import {
  SectionFields,
  draftToInput,
  emptyDraft,
  type SectionDraft,
} from '../components/SectionFields'

/**
 * A draft plus React list identity. The key exists because these sections have
 * no server id yet, and it stays out of SectionDraft because it has no business
 * on the wire.
 */
interface DraftSection extends SectionDraft {
  key: string
}

const newSection = (kind: SectionKind): DraftSection => ({
  key: `${Date.now()}-${Math.random()}`,
  ...emptyDraft(kind),
})

const inDays = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return toLocalInput(d)
}

/**
 * The owner-facing builder. It calls the management API, which authenticates
 * nobody — reaching this page must be gated by the proxy in front, not here.
 */
export function NewDropbox() {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [maxSizeMb, setMaxSizeMb] = useState('1024')
  const [softDeadline, setSoftDeadline] = useState(inDays(7))
  const [hardDeadline, setHardDeadline] = useState(inDays(30))
  const [sections, setSections] = useState<DraftSection[]>([newSection('files')])

  const create = useMutation({
    mutationFn: () =>
      managementApi.create({
        title: title.trim(),
        description: description.trim(),
        ownerEmail: ownerEmail.trim(),
        recipientEmail: recipientEmail.trim(),
        maxSizeMb: Number(maxSizeMb),
        softDeadline: fromLocalInput(softDeadline),
        hardDeadline: fromLocalInput(hardDeadline),
        sections: sections.map(draftToInput),
      }),
  })

  const patch = (key: string, changes: Partial<DraftSection>) =>
    setSections((prev) => prev.map((s) => (s.key === key ? { ...s, ...changes } : s)))

  const move = (index: number, delta: number) =>
    setSections((prev) => {
      const next = [...prev]
      const target = index + delta
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })

  if (create.isSuccess) {
    return (
      <PageShell>
        <Alert tone="success" title="Dropbox created">
          {create.data.dropbox.recipientEmail ? (
            <>
              The recipient link is on its way to{' '}
              <strong>{create.data.dropbox.recipientEmail}</strong>. Keep the admin link — it is the
              only way back in once the recipient link expires.
            </>
          ) : (
            <>
              Send the recipient link to whoever should upload. Keep the admin link — it is the only
              way back in once the recipient link expires.
            </>
          )}
        </Alert>
        <div className="card mt-4 space-y-4 p-5 sm:p-6">
          <CopyField label="Recipient link" value={toCurrentOrigin(create.data.links.recipientUrl)} />
          <CopyField label="Admin link" value={toCurrentOrigin(create.data.links.adminUrl)} />
          <div className="flex gap-2 pt-1">
            <a href={toCurrentOrigin(create.data.links.adminUrl)} className="btn-primary">
              Open admin view
            </a>
            <button type="button" onClick={() => create.reset()} className="btn-ghost">
              Create another
            </button>
          </div>
        </div>
      </PageShell>
    )
  }

  const problems = (create.error instanceof ApiError && Array.isArray(create.error.details)
    ? (create.error.details as string[])
    : null)

  return (
    <PageShell>
      <header className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">New dropbox</h1>
        <p className="mt-2 text-zinc-600 dark:text-zinc-400">
          Define what you need, then send the generated link. The recipient never needs an account.
        </p>
      </header>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate()
        }}
      >
        <div className="card space-y-4 p-5 sm:p-6">
          <div>
            <label className="label" htmlFor="title">
              Title
            </label>
            <input
              id="title"
              required
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Q3 audit documents"
            />
          </div>

          <div>
            <label className="label" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              rows={2}
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What the recipient should know before starting"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="email">
                Notify this address
              </label>
              <input
                id="email"
                type="email"
                required
                className="input"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="label" htmlFor="recipient-email">
                Email the link to (optional)
              </label>
              <input
                id="recipient-email"
                type="email"
                className="input"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="them@example.com"
              />
              <p className="mt-1 text-xs text-zinc-500">
                We send them the upload link. Leave it blank to send it yourself.
              </p>
            </div>
            <div>
              <label className="label" htmlFor="size">
                Maximum total size (MB)
              </label>
              <input
                id="size"
                type="number"
                min={1}
                required
                className="input"
                value={maxSizeMb}
                onChange={(e) => setMaxSizeMb(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="soft">
                Upload deadline
              </label>
              <input
                id="soft"
                type="datetime-local"
                required
                className="input"
                value={softDeadline}
                onChange={(e) => setSoftDeadline(e.target.value)}
              />
              <p className="mt-1 text-xs text-zinc-500">After this, no more uploads or edits.</p>
            </div>
            <div>
              <label className="label" htmlFor="hard">
                Access deadline
              </label>
              <input
                id="hard"
                type="datetime-local"
                required
                className="input"
                value={hardDeadline}
                onChange={(e) => setHardDeadline(e.target.value)}
              />
              <p className="mt-1 text-xs text-zinc-500">After this, the recipient link stops working.</p>
            </div>
          </div>
        </div>

        {sections.map((section, i) => (
          <div key={section.key} className="card space-y-4 p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">
                Section {i + 1} · {section.kind === 'files' ? 'File upload' : 'Text editor'}
              </h2>
              <div className="flex gap-1 text-xs">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="btn-ghost px-2 py-1">
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === sections.length - 1}
                  className="btn-ghost px-2 py-1"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => setSections((prev) => prev.filter((s) => s.key !== section.key))}
                  disabled={sections.length === 1}
                  className="btn-danger px-2 py-1"
                >
                  Remove
                </button>
              </div>
            </div>

            <SectionFields
              value={section}
              onChange={(changes) => patch(section.key, changes)}
              idPrefix={`new-${i}`}
            />
          </div>
        ))}

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setSections((p) => [...p, newSection('files')])} className="btn-ghost">
            + File section
          </button>
          <button type="button" onClick={() => setSections((p) => [...p, newSection('text')])} className="btn-ghost">
            + Text section
          </button>
        </div>

        {create.isError && (
          <Alert tone="error" title="Could not create the dropbox">
            {problems ? (
              <ul className="list-disc pl-5">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : create.error instanceof ApiError ? (
              create.error.message
            ) : (
              'Unexpected error.'
            )}
          </Alert>
        )}

        <div className="sticky bottom-0 -mx-4 border-t border-zinc-200 bg-white/85 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6 dark:border-zinc-800 dark:bg-zinc-950/85">
          <button type="submit" disabled={create.isPending} className="btn-primary w-full sm:w-auto">
            {create.isPending && <Spinner />}
            Create dropbox
          </button>
        </div>
      </form>
    </PageShell>
  )
}
